import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import FindingsList from "./FindingsList"; // Import the FindingsList component

const categorizeFindings = (findings) => {
  const categories = {
    Network: [],
    "Account & IAM": [],
    "Data Protection": [],
    Observability: [],
    DevOps: [],
    Compute: [],
    Database: [],
    "Secrets Management": [],
    "Unused Resources": [],
  };

  findings.forEach((finding) => {
    const service = finding.service?.S || finding.service || '';
    const description = (finding.description?.S || finding.description || '').toLowerCase();
    const resourceName = (finding.resourceName?.S || finding.resourceName || '').toLowerCase();

    // Network
    if (["ec2", "vpc", "elb"].includes(service) || 
        description.includes("network") || description.includes("port") || 
        description.includes("security group") || description.includes("acl") ||
        description.includes("vpc") || service === "route53") {
      categories.Network.push(finding);
    }
    // Observability (Logging & Monitoring)
    else if (["config", "cloudtrail", "cloudwatch"].includes(service) ||
             description.includes("cloudtrail") || description.includes("cloudwatch") ||
             description.includes("log") || description.includes("metric filter") ||
             description.includes("alarm") || description.includes("logging")) {
      categories.Observability.push(finding);
    }
    // Data Protection (Encryption, Backup)
    else if (["s3", "kms", "backup"].includes(service) ||
             description.includes("encryption") || description.includes("backup") ||
             description.includes("mfa delete") || description.includes("versioning")) {
      categories["Data Protection"].push(finding);
    }
    // Secrets Management
    else if (description.includes("secret") || description.includes(" key") ||
             description.includes("password") || description.includes("credential") ||
             description.includes("access key") || description.includes("mfa")) {
      categories["Secrets Management"].push(finding);
    }
    // Database
    else if (["rds", "dynamodb"].includes(service)) {
      categories.Database.push(finding);
    }
    // Compute
    else if (["lambda", "eks", "emr"].includes(service) ||
             description.includes("lambda") || description.includes("ec2") ||
             description.includes("instance")) {
      categories.Compute.push(finding);
    }
    // DevOps
    else if (["codepipeline", "codebuild", "codedeploy"].includes(service) ||
             description.includes("codedeploy") || description.includes("codebuild")) {
      categories.DevOps.push(finding);
    }
    // Unused Resources
    else if (description.includes("unused") || description.includes("idle") ||
             description.includes("not being used") || description.includes("unattached")) {
      categories["Unused Resources"].push(finding);
    }
    // Account & IAM (default for IAM-related findings)
    else if (service === "iam" || service === "account" ||
             description.includes("iam") || description.includes("policy") ||
             description.includes("role") || description.includes("user") ||
             description.includes("group") || description.includes("permission")) {
      categories["Account & IAM"].push(finding);
    }
    // Fallback to Account & IAM
    else {
      categories["Account & IAM"].push(finding);
    }
  });

  return categories;
};

const AttackSurface = ({ findingsData }) => {
  const [categorizedFindings, setCategorizedFindings] = useState({});
  const [cardData, setCardData] = useState([
    { id: 1, icon: "/icons/risk-categories/networking.svg", value: 0, label: "Network", color: "#E6E6FA" },
    { id: 2, icon: "/icons/risk-categories/identity.svg", value: 0, label: "Account & IAM", color: "#FFFACD" },
    { id: 3, icon: "/icons/risk-categories/data-protection.svg", value: 0, label: "Data Protection", color: "#FFDBAC" },
    { id: 4, icon: "/icons/risk-categories/monitoring.svg", value: 0, label: "Observability", color: "#B0E0E6" },
    { id: 5, icon: "/icons/risk-categories/forensics.svg", value: 0, label: "DevOps", color: "#E0FFFF" },
    { id: 6, icon: "/icons/risk-categories/monitoring.svg", value: 0, label: "Compute", color: "#F5F5DC" },
    { id: 7, icon: "/icons/risk-categories/storage.svg", value: 0, label: "Database", color: "#E0FFE0" },
    { id: 8, icon: "/icons/risk-categories/secrets.svg", value: 0, label: "Secrets Management", color: "#FFECDE" },
    { id: 9, icon: "/icons/risk-categories/storage.svg", value: 0, label: "Unused Resources", color: "#CCCCFE" },
  ]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (findingsData && findingsData.length > 0) {
      console.log('AttackSurface - First finding:', JSON.stringify(findingsData[0], null, 2));
      console.log('AttackSurface - Total findings:', findingsData.length);
      const categories = categorizeFindings(findingsData);
      console.log('AttackSurface - Categories:', Object.keys(categories).map(k => `${k}: ${categories[k].length}`));
      setCategorizedFindings(categories);

      const updatedCardData = cardData.map((card) => ({
        ...card,
        value: categories[card.label]?.length || 0,
      }));

      setCardData(updatedCardData);
    } else {
      console.log('AttackSurface - No findings data');
      // Reset to 0 when no data
      const resetCardData = cardData.map((card) => ({
        ...card,
        value: 0,
      }));
      setCardData(resetCardData);
      setCategorizedFindings({});
    }
  }, [findingsData]);
  // console.log("Complete JSON Data:", JSON.stringify(findingsData, null, 2));

  const handleCardClick = (category) => {
    setSelectedCategory(category === selectedCategory ? null : category);
  };

  const handleReset = () => {
    setSelectedCategory(null);
  };

  return (
    <div className="container p-0">
      <h2 className="mb-4" style={{ color: "rgba(33, 37, 41, 0.75)" }}>
        Risk Surface Categories
      </h2>
      <div className="row g-4">
        {cardData.map((card) => (
          <div
            key={card.id}
            className="col-6 col-sm-4 col-md-3 col-lg-2"
            onClick={() => handleCardClick(card.label)}
            style={{ cursor: "pointer" }}
          >
            <div
              style={{
                backgroundColor: selectedCategory === card.label ? "#f0f8ff" : "#fff",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
                borderBottom: `4px solid ${card.color}`,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "60px",
                  height: "60px",
                  marginBottom: "15px",
                }}
              >
                <img 
                  src={card.icon} 
                  alt={card.label} 
                  style={{ 
                    width: "48px", 
                    height: "48px"
                  }} 
                />
              </div>

              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                {card.value}
              </div>

              <div style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "10px" }}>
                {card.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 position-relative">
        <div className="position-absolute mx-2 mt-lg-2 mt-3" style={{ right: "0"}}>
          <button
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={!selectedCategory} // Disable the button when no category is selected
            style={
              !selectedCategory
                ? {
                    fontWeight: "bold",
                    backgroundColor: "#6D5BFF",
                    color: "#fff",
                    border:"1px solid #ccc",
                    cursor: "not-allowed",
                  }
                : {
                    fontWeight: "bold",
                    backgroundColor: "#fff",
                    color: "#6D5BFF",
                    border:"1px solid #fff",
                    cursor: "pointer",
                  }
            }
          >
            Show All
          </button>
        </div>
        <FindingsList
          findingsData={
            selectedCategory ? categorizedFindings[selectedCategory] || [] : findingsData
          }
          findingCategory={selectedCategory}
        />
      </div>
    </div>
  );
};

export default AttackSurface;
